import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AppShell,
  Avatar,
  Badge,
  Brand,
  Callout,
  BrandMark,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
  Sidebar,
  Spinner,
  ThemeToggle,
  Topbar,
  useShell,
  type NavGroupDef,
} from '@bifeai/ui';
import {
  Bell,
  Bot,
  Bug,
  ChartColumn,
  ChevronDown,
  CircleDollarSign,
  Inbox,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Store,
  Tags,
  Target,
  Users,
  Wallet,
  Handshake,
} from 'lucide-react';
import { useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { api } from '../lib/api';
import { platformCan, PLATFORM_ROLE_LABELS, type PlatformPerm, type SellerPortalDTO } from '@bifeai/shared';
import { useMeQuery } from '../lib/session';
import { ChangePasswordDialog } from './ChangePasswordDialog';

const TITLES: Record<string, string> = {
  '/': 'Inicio',
  '/estadisticas': 'Estadísticas',
  '/bifi': 'Estadísticas de bifi',
  '/clientes': 'Clientes',
  '/consultas': 'Consultas',
  '/ventas': 'Ventas',
  '/precios': 'Precios',
  '/avisos': 'Avisos',
  '/comunicacion': 'Comunicación',
  '/errores': 'Errores',
  '/agentes': 'Agentes IA',
  '/equipo': 'Equipo',
  '/vendedores': 'Vendedores',
  '/partners': 'Partners',
  '/mis-clientes': 'Mis clientes',
  '/prospectos': 'Prospectos',
  '/comisiones': 'Comisiones',
  '/mi-panel': 'Mi panel',
  '/mis-prospectos': 'Prospectos',
  '/mis-comisiones': 'Comisiones',
  '/mi-equipo': 'Mi red',
};

function SidebarBrand({ suffix }: { suffix: string }) {
  const { collapsed } = useShell();
  return <Link to="/">{collapsed ? <BrandMark /> : <Brand suffix={suffix} />}</Link>;
}

/** Panel admin: app separada de la de clientes, con su propio login y 2FA obligatoria. */
export function AdminLayout() {
  const me = useMeQuery();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [pwOpen, setPwOpen] = useState(false);
  const errors = useQuery({
    queryKey: ['errors', 'summary'],
    queryFn: () => api.get<{ open: number; new: number }>('/platform/errors/summary'),
    enabled: Boolean(me.data?.session.mfaVerified && !me.data.admin.mustChangePassword && platformCan(me.data.admin.role, 'errors.view')),
    refetchInterval: 30_000,
  });
  // Avisos de ventas: compras, sucursales nuevas, renovaciones, pagos rechazados.
  const alerts = useQuery({
    queryKey: ['billing', 'alerts', 'summary'],
    queryFn: () => api.get<{ unread: number; unreadErrors: number }>('/platform/billing/alerts/summary'),
    enabled: Boolean(me.data?.session.mfaVerified && !me.data.admin.mustChangePassword && platformCan(me.data.admin.role, 'alerts.view')),
    refetchInterval: 60_000,
  });
  const isSeller = me.data?.admin.role === 'SELLER';
  const portal = useQuery({
    queryKey: ['portal'],
    queryFn: () => api.get<SellerPortalDTO>('/platform/portal'),
    enabled: Boolean(isSeller && me.data?.session.mfaVerified && !me.data.admin.mustChangePassword),
  });

  if (me.isPending) {
    return (
      <div className="bf-center-screen">
        <Spinner />
      </div>
    );
  }
  if (me.isError) return <Navigate to="/ingresar" replace />;
  if (!me.data.session.mfaVerified) {
    return <Navigate to={me.data.session.mfaSetupRequired ? '/activar-2fa' : '/ingresar/codigo'} replace />;
  }

  const at = (p: string) => (p === '/' ? pathname === '/' : pathname.startsWith(p));
  if (isSeller && pathname === '/') return <Navigate to="/mi-panel" replace />;
  const role = me.data.admin.role;
  const ROUTE_PERM: Record<string, PlatformPerm | null> = {
    '/': null,
    '/estadisticas': 'stats.view',
    '/bifi': 'stats.view',
    '/clientes': 'tenants.view',
    '/consultas': 'leads.view',
    '/ventas': 'sales.view',
    '/precios': 'prices.view',
    '/avisos': 'alerts.view',
    '/comunicacion': 'comms.view',
    '/errores': 'errors.view',
    '/agentes': 'errors.view',
    '/equipo': 'team.manage',
    '/vendedores': 'sellers.view',
    '/partners': 'sellers.view',
    '/mis-clientes': 'portal.seller',
    '/prospectos': 'sellers.view',
    '/comisiones': 'sellers.view',
    '/mi-panel': 'portal.seller',
    '/mis-prospectos': 'portal.seller',
    '/mis-comisiones': 'portal.seller',
    '/mi-equipo': 'portal.seller',
  };
  const allowed = (to: string) => {
    const p = ROUTE_PERM[to];
    // Vendedores y partners: solo su portal.
    if (role === 'SELLER') return p === 'portal.seller';
    return !p || platformCan(role, p);
  };
  const sellerGroups: NavGroupDef[] = [
    {
      key: 'portal',
      items: [
        { key: 'mine', to: '/mi-panel', label: 'Mi panel', icon: <LayoutDashboard />, active: at('/mi-panel') },
        { key: 'mprospects', to: '/mis-prospectos', label: 'Prospectos', icon: <Target />, active: at('/mis-prospectos') },
        { key: 'mcomm', to: '/mis-comisiones', label: 'Comisiones', icon: <Wallet />, active: at('/mis-comisiones') },
        ...(portal.data?.seller.visibility === 'PORTFOLIO'
          ? [{ key: 'mclients', to: '/mis-clientes', label: 'Mis clientes', icon: <Store />, active: at('/mis-clientes') }]
          : []),
        ...(portal.data?.seller.isLeader ? [{ key: 'mteam', to: '/mi-equipo', label: 'Mi red', icon: <Users />, active: at('/mi-equipo') }] : []),
      ],
    },
  ];
  const allGroups: NavGroupDef[] = [
    {
      key: 'main',
      items: [
        { key: 'home', to: '/', label: 'Inicio', icon: <LayoutDashboard />, active: at('/') },
        { key: 'stats', to: '/estadisticas', label: 'Estadísticas', icon: <ChartColumn />, active: at('/estadisticas') },
        { key: 'bifi', to: '/bifi', label: 'Estadísticas de bifi', icon: <Bot />, active: at('/bifi') },
      ],
    },
    {
      key: 'clients',
      title: 'Clientes',
      items: [
        { key: 'tenants', to: '/clientes', label: 'Clientes', icon: <Store />, active: at('/clientes') },
        { key: 'leads', to: '/consultas', label: 'Consultas', icon: <Inbox />, active: at('/consultas') },
      ],
    },
    {
      key: 'sales',
      title: 'Ventas y cobros',
      items: [
        { key: 'sales', to: '/ventas', label: 'Ventas', icon: <CircleDollarSign />, active: at('/ventas') },
        { key: 'prices', to: '/precios', label: 'Precios', icon: <Tags />, active: at('/precios') },
      ],
    },
    {
      key: 'sellers',
      title: 'Vendedores y partners',
      items: [
        { key: 'partners', to: '/partners', label: 'Partners', icon: <Handshake />, active: at('/partners') },
        { key: 'sellers', to: '/vendedores', label: 'Vendedores', icon: <Users />, active: at('/vendedores') },
        { key: 'prospects', to: '/prospectos', label: 'Prospectos', icon: <Target />, active: at('/prospectos') },
        { key: 'commissions', to: '/comisiones', label: 'Comisiones', icon: <Wallet />, active: at('/comisiones') },
      ],
    },
    {
      key: 'comms',
      title: 'Avisos y comunicación',
      items: [
        { key: 'alerts', to: '/avisos', label: 'Avisos', icon: <Bell />, count: alerts.data?.unread || undefined, active: at('/avisos') },
        { key: 'news', to: '/comunicacion', label: 'Comunicación', icon: <Megaphone />, active: at('/comunicacion') },
      ],
    },
    {
      key: 'system',
      title: 'Sistema',
      items: [
        { key: 'errors', to: '/errores', label: 'Errores', icon: <Bug />, count: errors.data?.open, active: at('/errores') },
        { key: 'agents', to: '/agentes', label: 'Agentes IA', icon: <Bot />, active: at('/agentes') },
        { key: 'team', to: '/equipo', label: 'Equipo', icon: <Users />, active: at('/equipo') },
      ],
    },
  ];
  const groups = (isSeller ? sellerGroups : allGroups)
    .map((g) => ({ ...g, items: g.items.filter((i) => allowed(i.to)) }))
    .filter((g) => g.items.length > 0);
  const routeKey = Object.keys(ROUTE_PERM).find((k) => k !== '/' && pathname.startsWith(k)) ?? '/';

  const logout = async () => {
    await api.post('/platform/auth/logout').catch(() => undefined);
    qc.clear();
    navigate('/ingresar', { replace: true });
  };

  return (
    <AppShell sidebar={<Sidebar brand={<SidebarBrand suffix={isSeller ? 'Partners' : 'Admin'} />} groups={groups} Link={Link} />}>
      <Topbar title={TITLES[pathname] ?? 'Panel admin'}>
        {(errors.data?.new ?? 0) > 0 && (
          <Link to="/errores" className="bf-hide-mobile" style={{ textDecoration: 'none' }}>
            <Badge tone="danger" dot>
              {errors.data?.new} {errors.data?.new === 1 ? 'error nuevo' : 'errores nuevos'}
            </Badge>
          </Link>
        )}
        {(alerts.data?.unread ?? 0) > 0 && (
          <Link to="/avisos" className="bf-hide-mobile" style={{ textDecoration: 'none' }} aria-label="Avisos">
            <Badge tone={alerts.data?.unreadErrors ? 'danger' : 'warning'} dot>
              <Bell size={13} aria-hidden /> {alerts.data?.unread} {alerts.data?.unread === 1 ? 'aviso' : 'avisos'}
            </Badge>
          </Link>
        )}
        <span className="bf-hide-mobile">
          <ThemeToggle />
        </span>
        <Menu>
          <MenuTrigger asChild>
            <button type="button" className="bf-user-btn" aria-label={`Cuenta de ${me.data.admin.name}`}>
              <Avatar name={me.data.admin.name} />
              <ChevronDown />
            </button>
          </MenuTrigger>
          <MenuContent>
            <MenuLabel>
              <strong style={{ display: 'block', color: 'var(--bf-ink)', fontSize: 'var(--bf-text-md)' }}>{me.data.admin.name}</strong>
              {me.data.admin.email}
            </MenuLabel>
            <MenuSeparator />
            <MenuItem icon={<KeyRound />} onSelect={() => setPwOpen(true)}>
              Cambiar contraseña
            </MenuItem>
            <MenuItem icon={<LogOut />} danger onSelect={() => void logout()}>
              Cerrar sesión
            </MenuItem>
          </MenuContent>
        </Menu>
      </Topbar>
      <main className="bf-content">
        {me.data.admin.mustChangePassword ? (
          <Callout tone="warning">Entraste con una contraseña temporal. Cambiala para empezar a usar el panel.</Callout>
        ) : allowed(routeKey) ? (
          <Outlet />
        ) : (
          <Callout tone="warning">Tu rol ({PLATFORM_ROLE_LABELS[role]}) no tiene acceso a esta sección.</Callout>
        )}
      </main>
      <ChangePasswordDialog
        open={pwOpen || !!me.data.admin.mustChangePassword}
        forced={!!me.data.admin.mustChangePassword}
        onClose={() => {
          setPwOpen(false);
          void qc.invalidateQueries({ queryKey: ['me'] });
        }}
      />
    </AppShell>
  );
}
